import React from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import * as moment from "moment";
import FileIcon from "Components/Common/FileIcon";
import { PSEA_CORE_STANDARDS } from "./pseaCoreStandards";
import { isStandardScored, pseaAnswer } from "./pseaScoring";
import PSEAAssessmentHistory from "./PSEAAssessmentHistory";
import PSEAAssessmentHeader from "./PSEAAssessmentHeader";
import PSEAMinimumRequirements from "./PSEAMinimumRequirements";

const DetailTile = ({
  icon,
  label,
  children,
}: {
  icon?: string;
  label: string;
  children: any;
}) => (
  <div className="d-flex align-items-start gap-3">
    {icon ? (
      <div className="flex-shrink-0">
        <div className="avatar-sm">
          <div className="avatar-title bg-primary-subtle text-primary rounded fs-18">
            <i className={icon}></i>
          </div>
        </div>
      </div>
    ) : null}
    <div className="flex-grow-1 overflow-hidden">
      <p className="text-uppercase text-muted fw-medium fs-11 mb-1">{label}</p>
      <div className="fs-14 fw-medium">{children}</div>
    </div>
  </div>
);

/**
 * `embedded` renders the variant used inside the contractor page's PSEA tab,
 * where the contractor is already identified by the page header above. That
 * variant leads with the rating instead of repeating the contractor, and drops
 * the date/agency summary line since the Completed By card alongside it
 * already carries both.
 */
const PSEAAssessmentDetails = ({
  data,
  embedded,
}: {
  data: any;
  embedded?: boolean;
}) => {
  if (!data || !data.id) {
    return null;
  }

  const enteredByName = [data?.createdby?.firstName, data?.createdby?.lastName]
    .filter(Boolean)
    .join(" ");

  // Coverage across the 8 core standards drives the summary strip on the hero.
  const standards = PSEA_CORE_STANDARDS.map((standard) => {
    const cs = (data?.coreStandards || []).find(
      (c: any) => Number(c?.standardNumber) === standard.number
    );
    const evidence: string[] = cs?.evidence || [];
    const docs = cs?.supportingDocuments || [];
    return {
      standard,
      cs,
      evidence,
      docs,
      hasEvidence: evidence.length > 0,
      answer: pseaAnswer(cs),
      scored: isStandardScored(cs),
    };
  });

  // Jumps from the summary card to the matching standard further down.
  // Scrolls the window explicitly rather than using scrollIntoView, which
  // resolves to a non-scrolling ancestor on this layout and silently no-ops.
  const scrollToStandard = (standardNumber: number) => {
    const target = document.getElementById(`psea-standard-${standardNumber}`);
    if (!target) return;

    // Offset clears the fixed page header.
    const top = target.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  };

  return (
    <React.Fragment>
      <PSEAAssessmentHeader
        data={data}
        standards={standards}
        onSelect={scrollToStandard}
        embedded={embedded}
      />

      <Row>
        <Col xl={8}>
          {/* PSEA Information - Core Standards */}
          <Card>
            <CardHeader className="d-flex align-items-center">
              <h5 className="card-title mb-0 text-uppercase fw-semibold flex-grow-1">
                PSEA Information{" "}
                <span className="text-muted fw-semibold text-uppercase">
                  (8 Core Standards)
                </span>
              </h5>
            </CardHeader>
            <CardBody>
              <div className="vstack gap-3">
                {standards.map(
                  ({
                    standard,
                    cs,
                    evidence,
                    docs,
                    hasEvidence,
                    answer,
                    scored,
                  }) => {
                    return (
                      <div
                        key={standard.number}
                        id={`psea-standard-${standard.number}`}
                        className="border rounded overflow-hidden material-shadow-none"
                      >
                        {/* Standard header */}
                        <div className="d-flex align-items-center gap-3 p-3 bg-light border-bottom">
                          <div className="flex-shrink-0">
                            <div
                              className={`psea-standard-number avatar-sm d-flex align-items-center justify-content-center rounded-circle fw-bold fs-15 ${
                                scored
                                  ? "bg-primary text-white"
                                  : "bg-body text-muted border"
                              }`}
                            >
                              {standard.number}
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 fw-semibold">
                              {standard.title}
                            </h6>
                            <small className="text-muted">
                              Core Standard {standard.number}
                            </small>
                          </div>
                          <div className="flex-shrink-0 d-flex align-items-center gap-2">
                            {answer === "yes" ? (
                              <span className="badge bg-primary-subtle text-primary fs-11">
                                <i className="ri-check-line align-bottom me-1"></i>
                                Met &middot; 1 point
                              </span>
                            ) : answer === "na" ? (
                              <span className="badge bg-primary-subtle text-primary fs-11">
                                <i className="ri-subtract-line align-bottom me-1"></i>
                                Not applicable &middot; 1 point
                              </span>
                            ) : answer === "no" ? (
                              <span className="badge bg-light text-muted fs-11 border">
                                <i className="ri-close-line align-bottom me-1"></i>
                                Not met &middot; 0 points
                              </span>
                            ) : (
                              <span className="badge bg-light text-muted fs-11 border">
                                Not answered
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Standard body */}
                        <div className="p-3">
                          {standard.description ||
                          standard.minimumRequirements ? (
                            // pre-wrap so multi-line descriptions keep their
                            // list formatting.
                            <p
                              className="text-muted fs-13 mb-3"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {standard.description}
                              {standard.minimumRequirements?.length ? (
                                <PSEAMinimumRequirements
                                  standardNumber={standard.number}
                                  requirements={standard.minimumRequirements}
                                />
                              ) : null}
                            </p>
                          ) : null}

                          <div className="mb-3">
                            <div className="text-uppercase text-muted fw-medium fs-11 mb-1">
                              Does the organization meet this standard?
                            </div>
                            <div className="fs-13 fw-medium">
                              {answer === "yes" ? (
                                <span className="text-primary">
                                  <i className="ri-check-line align-bottom me-1"></i>
                                  Yes
                                </span>
                              ) : answer === "na" ? (
                                <span className="text-primary">
                                  <i className="ri-subtract-line align-bottom me-1"></i>
                                  Not applicable
                                </span>
                              ) : answer === "no" ? (
                                <span>
                                  <i className="ri-close-line align-bottom me-1"></i>
                                  No
                                </span>
                              ) : (
                                <span className="text-muted fst-italic fw-normal">
                                  Not answered
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="text-uppercase text-muted fw-medium fs-11 mb-2">
                              Evidence provided
                            </div>
                            {hasEvidence ? (
                              <ul className="list-unstyled mb-0 vstack gap-1">
                                {evidence.map((e, i) => (
                                  <li
                                    key={i}
                                    className="fs-13 d-flex align-items-center"
                                  >
                                    <i className="ri-checkbox-circle-line text-primary me-2 flex-shrink-0"></i>
                                    <span>{e}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted fs-13 fst-italic">
                                {answer === "na"
                                  ? "This standard does not apply to the organization."
                                  : "No evidence provided for this standard."}
                              </span>
                            )}
                          </div>

                          {cs?.otherSpecify ? (
                            <div className="mb-3">
                              <div className="text-uppercase text-muted fw-medium fs-11 mb-1">
                                Other
                              </div>
                              <div className="fs-13">{cs.otherSpecify}</div>
                            </div>
                          ) : null}

                          {docs.length > 0 ? (
                            <div className="mb-3">
                              <div className="text-uppercase text-muted fw-medium fs-11 mb-2">
                                Supporting documents
                              </div>
                              <Row className="g-2">
                                {docs.map((doc: any, i: number) => (
                                  <Col xxl={6} lg={6} key={i}>
                                    <div className="border border-dashed rounded p-2 h-100">
                                      <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0 me-2">
                                          <div className="avatar-sm">
                                            <div className="avatar-title bg-light text-secondary rounded fs-20">
                                              <FileIcon fileName={doc?.name} />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex-grow-1 overflow-hidden">
                                          <h5 className="fs-13 mb-0">
                                            <Link
                                              to={`${process.env.REACT_APP_FILES_URL}${doc?.url}`}
                                              target="_blank"
                                              className="text-body text-truncate d-block"
                                            >
                                              {doc?.name}
                                            </Link>
                                          </h5>
                                        </div>
                                        <div className="flex-shrink-0 ms-2">
                                          <Link
                                            className="btn btn-icon btn-soft-primary btn-sm fs-16"
                                            to={`${process.env.REACT_APP_FILES_URL}${doc?.url}`}
                                            target="_blank"
                                          >
                                            <i className="ri-download-2-line"></i>
                                          </Link>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          ) : null}

                          {cs?.comment ? (
                            <div className="d-flex gap-2 p-2 rounded bg-light border-start border-3 border-primary">
                              <i className="ri-chat-quote-line text-primary fs-16"></i>
                              <div className="fs-13">
                                <span className="fw-medium">Comment: </span>
                                {cs.comment}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={4}>
          {/* Completed By */}
          <Card>
            <CardHeader className="d-flex align-items-center">
              <h5 className="card-title mb-0 text-uppercase fw-semibold flex-grow-1">
                Completed By
              </h5>
            </CardHeader>
            <CardBody>
              <Row className="g-4">
                <Col md={6} xl={12}>
                  <DetailTile icon="ri-user-line" label="Name">
                    {enteredByName || "-"}
                  </DetailTile>
                </Col>
                <Col md={6} xl={12}>
                  <DetailTile icon="ri-mail-line" label="Email">
                    {data?.createdby?.email ? (
                      <a
                        href={`mailto:${data.createdby.email}`}
                        className="text-body text-break"
                      >
                        {data.createdby.email}
                      </a>
                    ) : (
                      "-"
                    )}
                  </DetailTile>
                </Col>
                <Col md={6} xl={12}>
                  <DetailTile
                    icon="ri-earth-line"
                    label="On behalf of (Agency Assessing)"
                  >
                    {data?.agency?.name || "-"}
                  </DetailTile>
                </Col>
                <Col md={6} xl={12}>
                  <DetailTile
                    icon="ri-calendar-line"
                    label="Date of Assessment"
                  >
                    {data?.dateOfAssessment
                      ? moment(new Date(data?.dateOfAssessment)).format(
                          "DD MMM YYYY"
                        )
                      : "-"}
                  </DetailTile>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Assessment history */}
          <PSEAAssessmentHistory history={data?.history || []} />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default PSEAAssessmentDetails;
